'use client';

import { c as _c } from "react/compiler-runtime";
import type { FC, ReactElement } from 'react';
import { useState } from 'react';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTrigger } from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { SacCard, SacCardSkeleton } from '@sola/ark-skippy/molecules/sac-card/sac-card.tsx';
import { SacControls } from '@sola/ark-skippy/molecules/sac-controls/sac-controls.tsx';
import { SacList } from '@sola/ark-skippy/molecules/sac-list/sac-list.tsx';
import type { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { useDepartureSubChildSearchSheet } from './departure-sub-child-search-sheet.logic.ts';
export interface DepartureSubChildSearchSheetProps {
  departingChildDetails: ChildDetails;
  getChildHref: (childGlobalId: string) => string;
  onChildSelect?: (child: ChildAvailabilityItem) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  openTrigger?: ReactElement;
}
export interface DepartureSubChildSearchSheetContentProps {
  departingChildDetails: ChildDetails;
  getChildHref: (childGlobalId: string) => string;
  onChildSelect?: (child: ChildAvailabilityItem) => void;
}
const SameCenterChildrenSkeleton: FC<{
  loadingLabel: string;
  skeletonCount: number;
}> = t0 => {
  const $ = _c(7);
  const {
    loadingLabel,
    skeletonCount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton height={28} width={260} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-comfy-sm" aria-hidden="true">{t1}<div className="flex flex-col gap-snug-md"><Skeleton height={16} width="100%" /><Skeleton height={16} width="80%" /></div></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== skeletonCount) {
    t3 = <div className="grid grid-cols-1 md:grid-cols-2 gap-comfy-lg">{Array.from({
        length: skeletonCount
      }, _temp)}</div>;
    $[2] = skeletonCount;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== loadingLabel || $[5] !== t3) {
    t4 = <section className="flex flex-col gap-comfy-xl" aria-label={loadingLabel} aria-busy={true}>{t2}{t3}</section>;
    $[4] = loadingLabel;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
export const DepartureSubChildSearchSheetContent: FC<DepartureSubChildSearchSheetContentProps> = t0 => {
  const $ = _c(38);
  const {
    departingChildDetails,
    getChildHref,
    onChildSelect
  } = t0;
  const {
    sameCenterChildren,
    sameCenterChildrenLoading,
    hasSameCenterChildren,
    sameCenterSkeletonCount,
    sameCenterBadgeText,
    title,
    sameCenterTitle,
    sameCenterBody,
    sameCenterLoadingLabel,
    searchTitle,
    searchBody
  } = useDepartureSubChildSearchSheet(departingChildDetails);
  let t1;
  if ($[0] !== title) {
    t1 = <SheetHeader>{title}</SheetHeader>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== sameCenterChildrenLoading || $[3] !== sameCenterLoadingLabel || $[4] !== sameCenterSkeletonCount) {
    t2 = sameCenterChildrenLoading && <SameCenterChildrenSkeleton loadingLabel={sameCenterLoadingLabel} skeletonCount={sameCenterSkeletonCount} />;
    $[2] = sameCenterChildrenLoading;
    $[3] = sameCenterLoadingLabel;
    $[4] = sameCenterSkeletonCount;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== getChildHref || $[7] !== hasSameCenterChildren || $[8] !== onChildSelect || $[9] !== sameCenterBadgeText || $[10] !== sameCenterBody || $[11] !== sameCenterChildren || $[12] !== sameCenterTitle) {
    t3 = hasSameCenterChildren && <section className="flex flex-col gap-comfy-xl"><div className="flex flex-col gap-comfy-sm"><h2 className="heading-md label-neutral-amplified">{sameCenterTitle}</h2><p className="body-sm label-neutral-mild">{sameCenterBody}</p></div><div className="grid grid-cols-1 md:grid-cols-2 gap-comfy-lg">{sameCenterChildren.map(child => <SacCard key={child.globalId} {...child} badgeText={sameCenterBadgeText} href={getChildHref(child.globalId)} onChildSelect={onChildSelect} />)}</div></section>;
    $[6] = getChildHref;
    $[7] = hasSameCenterChildren;
    $[8] = onChildSelect;
    $[9] = sameCenterBadgeText;
    $[10] = sameCenterBody;
    $[11] = sameCenterChildren;
    $[12] = sameCenterTitle;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== searchTitle) {
    t4 = <h2 className="heading-md label-neutral-amplified">{searchTitle}</h2>;
    $[14] = searchTitle;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== searchBody) {
    t5 = <p className="body-sm label-neutral-mild">{searchBody}</p>;
    $[16] = searchBody;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <div className="flex flex-col gap-comfy-sm">{t4}{t5}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SacControls isDynamic={true} alwaysCollapseFilters={true} />;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== getChildHref) {
    t8 = child_0 => getChildHref(child_0.globalId);
    $[22] = getChildHref;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== onChildSelect || $[25] !== t8) {
    t9 = <SacList isDynamic={true} hideAssistedCtas={true} columnsMd="2" columnsLg="2" getChildHref={t8} onChildSelect={onChildSelect} />;
    $[24] = onChildSelect;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t6 || $[28] !== t9) {
    t10 = <section className="flex flex-col gap-comfy-xl">{t6}{t7}{t9}</section>;
    $[27] = t6;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t2 || $[32] !== t3) {
    t11 = <SheetBody><div className="flex flex-col gap-comfy-xl">{t2}{t3}{t10}</div></SheetBody>;
    $[30] = t10;
    $[31] = t2;
    $[32] = t3;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t1 || $[35] !== t11 || $[36] !== title) {
    t12 = <SheetContent title={title}>{t1}{t11}</SheetContent>;
    $[34] = t1;
    $[35] = t11;
    $[36] = title;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
export const DepartureSubChildSearchSheet: FC<DepartureSubChildSearchSheetProps> = t0 => {
  const $ = _c(14);
  let contentProps;
  let onOpenChange;
  let open;
  let openTrigger;
  if ($[0] !== t0) {
    ({
      open,
      onOpenChange,
      openTrigger,
      ...contentProps
    } = t0);
    $[0] = t0;
    $[1] = contentProps;
    $[2] = onOpenChange;
    $[3] = open;
    $[4] = openTrigger;
  } else {
    contentProps = $[1];
    onOpenChange = $[2];
    open = $[3];
    openTrigger = $[4];
  }
  const [internalOpen, setInternalOpen] = useState(false);
  const t1 = open ?? internalOpen;
  const t2 = onOpenChange ?? setInternalOpen;
  let t3;
  if ($[5] !== openTrigger) {
    t3 = openTrigger && <SheetTrigger>{openTrigger}</SheetTrigger>;
    $[5] = openTrigger;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== contentProps) {
    t4 = <DepartureSubChildSearchSheetContent {...contentProps} />;
    $[7] = contentProps;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <Sheet open={t1} onOpenChange={t2}>{t3}{t4}</Sheet>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
function _temp(_, index) {
  return <SacCardSkeleton key={index} />;
}